import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { Novel, Series, SeriesFormData } from '../../services/types';
import { SeriesEditorModal } from './SeriesEditorModal';
import { CoverImage } from '../CoverImage';

interface NovelSeriesManagerModalProps {
  novel: Novel;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => Promise<void>;
}

export function NovelSeriesManagerModal({
  novel,
  isOpen,
  onClose,
  onUpdate
}: NovelSeriesManagerModalProps) {
  const [series, setSeries] = useState<Series[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(novel.series_id || null);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 載入所有系列
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      setSelectedSeriesId(novel.series_id || null);
      api.getSeries()
        .then(setSeries)
        .catch((err) => setError(err instanceof Error ? err.message : '載入失敗'))
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, novel.series_id]);

  const handleSave = async () => {
    // 沒有變更
    if (selectedSeriesId === (novel.series_id || null)) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // 移出原系列
      if (novel.series_id) {
        await api.removeNovelFromSeries(novel.series_id, novel.id);
      }

      // 加入新系列
      if (selectedSeriesId) {
        await api.addNovelToSeries(selectedSeriesId, novel.id);
      }

      await onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失敗');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSeries = async (data: SeriesFormData) => {
    const newSeries = await api.createSeries(data);
    setSeries(prev => [newSeries, ...prev]);
    setSelectedSeriesId(newSeries.id);
    setShowCreateNew(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal">
        {/* Backdrop */}
        <div className="overlay" onClick={onClose} />

        {/* Modal */}
        <div className="modal-content max-w-md">
          {/* Header */}
          <div className="card-header">
            <h3 className="heading-section">管理系列歸屬</h3>
            <p className="body-small mt-1 truncate">{novel.title}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-5 mt-4 p-3 bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400 rounded-button text-sm">
              {error}
            </div>
          )}

          {/* Options */}
          <div className="p-4 space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="relative">
                  <div className="w-6 h-6 border-2 border-neutral-200 dark:border-neutral-700 rounded-full" />
                  <div className="absolute inset-0 w-6 h-6 border-2 border-transparent border-t-primary-500 rounded-full animate-spin" />
                </div>
              </div>
            ) : (
              <>
                {/* 無系列選項 */}
                <button
                  onClick={() => setSelectedSeriesId(null)}
                  className={`w-full p-3 rounded-button text-left transition-colors ${
                    selectedSeriesId === null
                      ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  <div className="font-medium text-neutral-800 dark:text-white">
                    不屬於任何系列
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">
                    移除此小說的系列關聯
                  </div>
                </button>

                {/* 系列列表 */}
                {series.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSeriesId(s.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-button text-left transition-colors ${
                      selectedSeriesId === s.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                    }`}
                  >
                    {/* 封面縮圖 */}
                    <div className="w-10 h-14 bg-neutral-100 dark:bg-neutral-600 rounded overflow-hidden flex-shrink-0">
                      <CoverImage
                        src={s.cover_url}
                        alt={s.title}
                        className="w-full h-full object-cover"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm font-bold">
                            {s.title.charAt(0)}
                          </div>
                        }
                      />
                    </div>

                    {/* 資訊 */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-800 dark:text-white truncate">
                        {s.title}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        {s.author || '未知作者'} · {s.novel_count} 本
                      </div>
                    </div>

                    {/* 選中標記 */}
                    {selectedSeriesId === s.id && (
                      <svg
                        className="w-5 h-5 text-primary-600 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}

                {/* 新增系列 */}
                <button
                  onClick={() => setShowCreateNew(true)}
                  className="w-full p-3 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-button text-neutral-600 dark:text-neutral-400 hover:border-primary-500 hover:text-primary-600 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  建立新系列
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="card-footer flex items-center justify-end gap-3">
            <button onClick={onClose} className="btn btn-ghost btn-md">
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary btn-md"
            >
              {isSaving ? '儲存中...' : '確定'}
            </button>
          </div>
        </div>
      </div>

      {/* 建立系列 Modal */}
      <SeriesEditorModal
        series={null}
        isOpen={showCreateNew}
        onClose={() => setShowCreateNew(false)}
        onSave={handleCreateSeries}
      />
    </>
  );
}
